import React, { useState, useCallback, useEffect } from 'react';
import { useWebSocketCall } from '../../../hooks/use-websocket-call';
import { cn } from '../../../utils';
import type { QCallInlineConfig, ConnectionState } from '../../../types';
import { StyleContainer } from '../shared';
import { BotIcon } from '../../shared/icons';

export interface ExpandedInlineWidgetProps {
  /** Widget configuration */
  config: QCallInlineConfig;
  /** Additional class names */
  className?: string;
}

/**
 * Expanded style inline widget - Updated to single animated box design
 */
export function ExpandedInlineWidget({ config, className }: ExpandedInlineWidgetProps) {
  const [callDuration, setCallDuration] = useState(0);

  const {
    connect,
    disconnect,
    connectionState,
    error,
    transcript,
  } = useWebSocketCall({
    wsUrl: config.wsUrl,
    assistantId: config.assistantId || '',
    userId: config.userId,
    workspaceId: config.workspaceId,
    apiBaseUrl: config.apiBaseUrl,
    language: config.language,
    voiceModal: config.voiceModal,
    voiceName: config.voiceName,
    onConnect: config.onConnect,
    onDisconnect: config.onDisconnect,
    onError: config.onError,
  });

  // Timer effect
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (connectionState === 'connected') {
      interval = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    } else {
      setCallDuration(0);
    }
    return () => clearInterval(interval);
  }, [connectionState]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleStartCall = useCallback(async () => {
    await connect();
  }, [connect]);

  const handleEndCall = useCallback(() => {
    disconnect();
  }, [disconnect]);

  return (
    <StyleContainer uiStyle="expanded" className={cn("flex flex-col items-center justify-center py-8 px-6 bg-transparent w-full min-h-[400px] h-full", className)}>

      {/* Main Premium Box */}
      <div className="relative w-full flex flex-col items-center gap-4 p-8 overflow-hidden rounded bg-[#0F172A] border border-white/10 shadow-[0_32px_64px_-16px_rgba(0,0,0,0.5)]">

        {/* Animated Background Atmosphere */}
        <div className="absolute inset-0 pointer-events-none">
          <div
            className={cn(
              "absolute -top-[10%] -right-[10%] w-[70%] h-[70%] bg-indigo-600/20 blur-[80px] transition-all duration-1000",
              connectionState === 'connected' ? "opacity-40 animate-pulse" : "opacity-20"
            )}
          />
          <div
            className={cn(
              "absolute -bottom-[10%] -left-[10%] w-[60%] h-[60%] bg-purple-600/20 blur-[80px] transition-all duration-1000",
              connectionState === 'connected' ? "opacity-30 animate-[pulse_4s_ease-in-out_infinite]" : "opacity-10"
            )}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-900/50 to-slate-900" />
        </div>

        {/* Top Header / Brand */}
        <div className="z-10 flex flex-col items-center gap-3">
          <div className="w-14 h-14 rounded-2xl bg-white/5 backdrop-blur-md border border-white/10 flex items-center justify-center shadow-inner overflow-hidden">
            {config.avatar ? (
              <img src={config.avatar} alt={config.agentName || 'AI Assistant'} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <BotIcon className="w-7 h-7 text-white" />
              </div>
            )}
          </div>
          <div className="text-center">
            <h3 className="text-white font-semibold text-[15px] tracking-tight">{config.agentName || 'AI Assistant'}</h3>
            <div className="flex items-center justify-center gap-1.5 mt-0.5">
              <div className={cn(
                "w-1.5 h-1.5 rounded-full",
                connectionState === 'connected' ? "bg-emerald-400 animate-pulse shadow-[0_0_8px_rgba(52,211,153,0.6)]" : "bg-slate-500"
              )} />
              <span className="text-slate-400 text-[10px] font-medium uppercase tracking-wider">
                {connectionState === 'connected' ? 'Live' : connectionState === 'connecting' ? 'Connecting' : 'Ready'}
              </span>
            </div>
          </div>
        </div>

        {/* Center Visualizer / Status Area */}
        <div className="z-10 flex-1 flex items-center justify-center w-full">
          <div className={cn(
            "relative w-24 h-24 flex items-center justify-center",
            connectionState === 'connected' && "scale-110 transition-transform duration-500"
          )}>
            {/* Outer Glows */}
            <div className={cn(
              "absolute inset-0 rounded-full bg-indigo-500/20 blur-xl transition-all duration-700",
              connectionState === 'connected' ? "scale-150 opacity-100" : "scale-0 opacity-0"
            )} />

            {/* Animated Avatar Circle */}
            <div className={cn(
              "w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg border-2 border-white/20 transition-all duration-500",
              connectionState === 'connected' ? "scale-110" : "scale-100"
            )}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="22" />
              </svg>
            </div>
          </div>
        </div>

        {/* Transcript Messages */}
        {transcript.length > 0 && (
          <div className="z-10 w-full max-h-[40vh] overflow-y-auto px-1.5 flex flex-col gap-1.5">
            {transcript.map((msg) => (
              <div
                key={msg.id}
                className={cn(
                  "p-2 rounded-lg text-[12px] leading-snug max-w-[85%]",
                  msg.speaker === 'user'
                    ? "bg-white/10 text-white/90 self-end"
                    : "bg-indigo-500/20 text-indigo-200 self-start"
                )}
              >
                <span className="font-medium text-[10px] block mb-0.5 opacity-70">
                  {msg.speaker === 'user' ? 'You' : config.agentName || 'AI'}
                </span>
                {msg.text}
              </div>
            ))}
          </div>
        )}

        {/* Bottom Control Area */}
        <div className="z-10 flex flex-col items-center gap-3">
          {connectionState !== 'connected' ? (
            <button
              className="py-3 px-8 rounded-2xl bg-white text-slate-900 font-bold text-sm text-center transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-lg disabled:opacity-50"
              onClick={handleStartCall}
              disabled={connectionState === 'connecting'}
            >
              {connectionState === 'connecting' ? 'Establishing connection...' : 'Start Voice Conversation'}
            </button>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <div className="px-4 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-md">
                <span className="text-white font-mono text-base tabular-nums">{formatTime(callDuration)}</span>
              </div>
              <button
                className="py-3 px-8 rounded-2xl bg-red-500/10 border border-red-500/50 text-red-500 font-bold text-sm hover:bg-red-500 hover:text-white transition-all duration-200 flex items-center justify-center gap-2"
                onClick={handleEndCall}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
                  <line x1="23" y1="1" x2="1" y2="23" />
                </svg>
                End Conversation
              </button>
            </div>
          )}

          <p className="text-slate-500 text-[9px] uppercase tracking-[0.2em] font-bold mb-1">Powered by QCALL AI</p>

          {/* Error message */}
          {error && (
            <div
              className="w-full max-w-[320px] p-3 rounded-xl text-left"
              style={{
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
              }}
            >
              <div className="flex items-start gap-2">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fca5a5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <p style={{ fontSize: '11px', color: '#fca5a5', lineHeight: 1.5 }}>
                  {error.message}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </StyleContainer>
  );
}

